import { useState, useEffect, useRef, useCallback, memo } from 'react';
import { FixedSizeList as List } from 'react-window';
import { v4 as uuidv4 } from "uuid";
import CommandCenterService from '../../../../../../service/CommandCenterService';
import useUrlParams from '../../../../hooks/useUrlParams';
import './Console.scss';
import { getEnvVar } from '../../../../../../envUtils';
import { get_formated_time } from '../../../../utils';
import { ArrowUp, BeaconSvg, HealthMetricSvg, InvalidTmSvg } from './ConsoleSvg';
import useScenario from '../../../../hooks/useScenario';
import CustomToolTip from '../../../../../CommonComponents/CustomToolTip';

const tctmTypeClasses = {
    'Beacon': 'bg-typeBeacon',
    'Health Metric': 'bg-typeHealthMetric',
    'TCTM': 'bg-typeTCTM',
    'TC': 'bg-typeTC',
    'TM': 'bg-typeTM',
    'Invalid TM': 'bg-invalidTM',
};

// Fetches command details from the server based on provided parameters and cursor.
const fetchEntries = async (sat_id, truetwin_id, scenario_id, cursor) => {
    try {
        const { data } = await CommandCenterService.getAllCommandDetails(
            sat_id,
            truetwin_id,
            scenario_id,
            cursor,
            50
        );
        return {
            data: data?.map(entry => ({ ...entry, uniqueId: uuidv4() })) || [],
            cursor: data?.[0]?.cursorId || ''
        };
    } catch (error) {
        console.error('Fetch error:', error);
        return { data: [], cursor: '' };
    }
};

// Memoized component to display the list of console output entries.
const ConsoleOuputList = memo(({ height, getCommandDetails, show_selected_output_details, selectedRow }) => {
    const { sat_id, truetwin_id, scenario_id } = useUrlParams();
    const { scenario } = useScenario();
    const [entries, setEntries] = useState([]);
    const [showScrollTop, setShowScrollTop] = useState(false);
    const loadingRef = useRef(false);
    const listRef = useRef(null);
    const cursorRef = useRef('');
    const isAtTopRef = useRef(true);

    const truncate_rows = useCallback((index) => {
        const commandIndexs = selectedRow.commandIndexs;
        if (commandIndexs.length <= 1) return '';
        return index === commandIndexs[0] ? 'request' :
            index === commandIndexs.at(-1) ? 'response' :
                index > commandIndexs[0] && index < commandIndexs.at(-1) ? 'truncate__row' : '';
    }, [selectedRow.commandIndexs]);

    // Loads the latest console entries from the server.
    const loadLatestEntries = useCallback(async () => {
        if (loadingRef.current) return;
        loadingRef.current = true;
        try {
            const { data, cursor } = await fetchEntries(sat_id, truetwin_id, scenario_id, '');
            setEntries(data);
            cursorRef.current = cursor;
            if (isAtTopRef.current) listRef.current?.scrollTo(0);
            show_selected_output_details(null, entries); // Pass entries to parent
        } finally {
            loadingRef.current = false;
        }
    }, [sat_id, truetwin_id, scenario_id]);

    // Loads more console entries from the server using the current cursor.
    const loadMoreEntries = useCallback(async () => {
        if (loadingRef.current || !cursorRef.current) return;
        loadingRef.current = true;
        try {
            const { data, cursor } = await fetchEntries(sat_id, truetwin_id, scenario_id, cursorRef.current);
            setEntries(prev => [...prev, ...data]);
            cursorRef.current = cursor;
        } finally {
            loadingRef.current = false;
        }
    }, [sat_id, truetwin_id, scenario_id]);

    // Handles scroll events from the list and loads more entries when needed.
    const handleScroll = useCallback(({ scrollOffset, scrollDirection }) => {
        setShowScrollTop(scrollOffset > 10);
        const list = listRef.current;
        if (!list) return;

        const totalHeight = entries?.length * list.props.itemSize;
        const bottomThreshold = 5;

        if (scrollDirection === 'forward' && scrollOffset + list.props.height >= totalHeight - bottomThreshold) {
            loadMoreEntries();
        } else if (scrollDirection === 'backward' && scrollOffset === 0) {
            loadLatestEntries();
        }
        isAtTopRef.current = scrollOffset === 0;
    }, [entries?.length, loadMoreEntries, loadLatestEntries]);

    // Establishes a WebSocket connection to receive real-time updates and load latest entries.
    useEffect(() => {
        const token = sessionStorage.getItem("authentication");
        if (!truetwin_id || !token) return;

        const sessionId = uuidv4();
        const ws = new WebSocket(`${getEnvVar('REACT_APP_WEBSOCKET_BACKEND_BASE_URL')}groundstation/websocketstatus?truetwinId=${truetwin_id}&sessionId=${sessionId}`);

        ws.onopen = () => ws.send(JSON.stringify({ type: 'authorization', token }));
        ws.onmessage = () => isAtTopRef.current && loadLatestEntries();
        ws.onerror = console.error;

        return () => ws.close();
    }, [truetwin_id, loadLatestEntries]);

    // Loads the latest entries when the component mounts.
    useEffect(() => { loadLatestEntries(); }, [loadLatestEntries]);

    // Memoized component to render a single row in the list.
    const Row = memo(({ index, style }) => {
        const entry = entries[index];
        const isSelected = entry?.uniqueId === selectedRow.CommandUid;
        const commandIndexs = selectedRow?.commandIndexs;
        const isHidden = commandIndexs?.length > 1 && index > commandIndexs[0] + 4 && index < commandIndexs.at(-1);

        const borderStyle = entry?.CommandDetails?.tctmType === "TCTM" ? {
            borderLeft: entry.type === "Tc" ? "2px solid #CCF54E" : "2px solid #36F097"
        } : {};
        return (
            <div style={style}>
                <div className={`table__body__row ${isHidden ? 'd-none' : 'd-flex'} ${tctmTypeClasses[entry?.CommandDetails?.tctmType] || ''}`}>
                    <button
                        className={`table__body__row__item ${truncate_rows(index)} ${isSelected ? 'selected' : ''}`}
                        onClick={() => {
                            if (!(isAtTopRef.current && loadingRef.current)) {
                                getCommandDetails(entry);
                                show_selected_output_details(entry, entries);
                            }
                        }}
                        style={borderStyle}
                    >
                        <TableCell width={90} content={entry?.CommandDetails?.tctmType}>
                            {entry?.CommandDetails?.tctmType === "Health Metric" && <HealthMetricSvg />}
                            {entry?.CommandDetails?.tctmType === "Beacon" && <BeaconSvg />}
                            {entry?.CommandDetails?.tctmType === "Invalid TM" && <InvalidTmSvg />}
                            <span className="truncate__text uppercase">{entry?.type}</span>
                        </TableCell>
                        <TableCell width={160} content={get_formated_time(entry?.CommandDetails?.timestamp)} />
                        <TableCell width={160} content={entry?.CommandDetails?.['groundTimestamp'] ? get_formated_time(entry?.CommandDetails?.['groundTimestamp'] / 1000) : '--'} />
                        <TableCell width={80} content={entry?.CommandDetails?.saId} />
                        <TableCell width={100} content={entry?.CommandDetails?.daId} />
                        <TableCell width={100} content={entry?.CommandDetails?.frameLength} />
                        <TableCell width={100} content={entry?.CommandDetails?.tctmId} />
                        <TableCell width={100} content={entry?.CommandDetails?.TmType ?? "--"} />
                        <TableCell width={100} content={entry?.CommandDetails?.GroundStationId ?? "--"} />
                        <TableCell width={140} content={entry?.contactGspId ?? '--'} />
                        <TableCell width={140} content={entry?.Info ?? '--'} />

                    </button>
                </div>
            </div>
        );
    });

    return (
        <>
            <List
                ref={listRef}
                height={height}
                itemCount={entries?.length}
                itemSize={35}
                width="100%"
                onScroll={handleScroll}
                overscanCount={5}
            >
                {Row}
            </List>
            <button
                onClick={() => { loadLatestEntries(); listRef.current?.scrollTo(0); }}
                title="Scroll to top"
                className={`scroll-to-top ${showScrollTop ? '' : 'hidden'}`}
            >
                <ArrowUp />
            </button>
        </>
    );
});
const TableCell = memo(({ width, content, children }) => (
    <div className="d-flex align-items-center justify-content-start" style={{ width: `${width}px` }}>
        <CustomToolTip title={content} placement="top">
            <span className="truncate__text">{children ?? content}</span>
        </CustomToolTip>
    </div>
));
export default ConsoleOuputList;